import { ExclamationCircleFilled } from '@ant-design/icons'
import { faCalendar, faPills, faPlus, faStethoscope, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal as AntModal, Button, Card, Col, Modal, Row, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect } from 'react'
import DesktopMedicalHistoryNav from './Nav'
import DatePicker from '../../../Reusable Components/DatePicker'
import useChronicIllnesses from '../../hooks/useChronicIllnesses'
import '../style/DesktopChronicIllnesses.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopChronicIllnesses = ({ clientForm, data, setFacet, setSaving }) => {
  const { Text } = Typography
  const {
    illnessList,
    handleAddIllness,
    handleRemoveIllness,
    handleUpdateIllness,
    handleRemoveMedication,
    handleAddMedication,
    handleUpdateMedication,
    nappiData,
    icd10Data,
    nappiLoading,
    icd10Loading,
    handleSubmit,
    setICD10SearchFilter,
    setNappiSearchFilter,
    showIllnessModal,
    setShowIllnessModal,
    medicationModalState,
    setMedicationModalState,
    editingIllness,
    setEditingIllness,
    tempDiagnosisDate,
    setTempDiagnosisDate,
    tempMedDates,
    setTempMedDates
  } = useChronicIllnesses({ clientForm, setFacet, setSaving })

  const [showIllnessDatePicker, setShowIllnessDatePicker] = useState(false)
  const [showMedStartDatePicker, setShowMedStartDatePicker] = useState(false)
  const [showMedEndDatePicker, setShowMedEndDatePicker] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    condition: false,
    diagnosisDate: false
  })

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  const showDeleteIllnessConfirm = (illness, index) => {
    AntModal.confirm({
      title: 'Remove Chronic Illness',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{illness.condition}</strong>?
          </p>
          <p>This action will also remove all associated medications.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveIllness(index)
      }
    })
  }

  const showDeleteMedicationConfirm = (medication, medicationIndex, illnessIndex) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div>
          <p>
            Are you sure you want to remove <strong>{medication.med.name}</strong>?
          </p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(medicationIndex, illnessIndex)
      }
    })
  }

  return (
    <div className='desktop-chronic-illnesses-container'>
      <DesktopSectionHeader
        icon={faStethoscope}
        title='Chronic Illnesses'
        subtitle='Track your ongoing medical conditions and medications'
      />

      <div className='desktop-chronic-illnesses-content'>
        {/* Add New Illness Button */}
        <Card className='desktop-add-illness-card'>
          <Button
            type='primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setShowIllnessModal(true)}
            size='large'
            className='desktop-add-illness-btn'
          >
            Add Chronic Illness
          </Button>
        </Card>

        {/* Illnesses List */}
        <Row gutter={[24, 24]}>
          {illnessList.map((illness, illnessIndex) => (
            <Col xs={24} lg={12} key={illnessIndex}>
              <Card className='desktop-illness-card'>
                <div className='desktop-illness-header'>
                  <div className='desktop-illness-info'>
                    <div className='desktop-illness-icon'>
                      <FontAwesomeIcon icon={faStethoscope} />
                    </div>
                    <div>
                      <h3 className='desktop-illness-name'>{illness.condition}</h3>
                      <Text className='desktop-diagnosis-date'>
                        Diagnosed: {illness.diagnosisDate ? dayjs(illness.diagnosisDate).format('MMM YYYY') : 'Unknown'}
                      </Text>
                    </div>
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => showDeleteIllnessConfirm(illness, illnessIndex)}
                    className='desktop-remove-illness-btn'
                  />
                </div>

                {/* Medications for this illness */}
                <div className='desktop-medications-section'>
                  <div className='desktop-medications-header'>
                    <h4>Medications</h4>
                    <Button
                      type='dashed'
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() =>
                        setMedicationModalState({
                          show: true,
                          illnessIndex,
                          isEditing: false
                        })
                      }
                      size='small'
                    >
                      Add Medication
                    </Button>
                  </div>

                  {illness.medications && illness.medications.length > 0 ? (
                    <div className='desktop-medications-list'>
                      {illness.medications.map((medication, medIndex) => (
                        <div key={medIndex} className='desktop-medication-item'>
                          <div className='desktop-medication-info'>
                            <FontAwesomeIcon icon={faPills} className='desktop-med-icon' />
                            <div>
                              <span className='desktop-med-name'>{medication.med.name}</span>
                              <div className='desktop-med-dates'>
                                <Text type='secondary'>
                                  {medication.startDate
                                    ? dayjs(medication.startDate).format('MMM YYYY')
                                    : 'Start: Unknown'}{' '}
                                  -{medication.endDate ? dayjs(medication.endDate).format('MMM YYYY') : ' Ongoing'}
                                </Text>
                              </div>
                            </div>
                          </div>
                          <Button
                            danger
                            type='text'
                            size='small'
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => showDeleteMedicationConfirm(medication, medIndex, illnessIndex)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type='secondary' className='desktop-no-medications'>
                      No medications added for this condition
                    </Text>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {illnessList.length === 0 && (
          <Card className='desktop-empty-state'>
            <div className='desktop-empty-content'>
              <FontAwesomeIcon icon={faStethoscope} className='desktop-empty-icon' />
              <h3>No Chronic Illnesses Added</h3>
              <p>Click "Add Chronic Illness" to start tracking your ongoing medical conditions.</p>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Illness Modal */}
      <Modal
        title={editingIllness !== null ? 'Edit Chronic Illness' : 'Add Chronic Illness'}
        open={showIllnessModal}
        onCancel={() => {
          setShowIllnessModal(false)
          setEditingIllness(null)
          setTempDiagnosisDate(null)
        }}
        footer={null}
        className='desktop-illness-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Condition Name</label>
            <Select
              showSearch
              placeholder='Search for a condition...'
              style={{ width: '100%' }}
              loading={icd10Loading}
              onSearch={setICD10SearchFilter}
              filterOption={false}
              notFoundContent={icd10Loading ? 'Searching...' : 'No conditions found'}
              onSelect={(value, option) => {
                const selectedCondition = icd10Data.find((item) => item.description === value)
                setEditingIllness(selectedCondition ? selectedCondition.description : value)
              }}
            >
              {icd10Data.map((item, index) => (
                <Select.Option key={index} value={item.description}>
                  {item.description}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className='desktop-form-field'>
            <label>Diagnosis Date</label>
            <Button block onClick={() => setShowIllnessDatePicker(true)} className='desktop-date-button'>
              <FontAwesomeIcon icon={faCalendar} />
              {tempDiagnosisDate ? dayjs(tempDiagnosisDate).format('MMMM YYYY') : 'Select diagnosis date'}
            </Button>
          </div>

          <div className='desktop-modal-actions'>
            <Button onClick={() => setShowIllnessModal(false)}>Cancel</Button>
            <Button type='primary' onClick={handleAddIllness}>
              {editingIllness !== null ? 'Update' : 'Add'} Illness
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Medication Modal */}
      <Modal
        title='Add Medication'
        open={medicationModalState.show}
        onCancel={() => setMedicationModalState({ show: false })}
        footer={null}
        className='desktop-medication-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Medication Name</label>
            <Select
              showSearch
              placeholder='Search for medication...'
              style={{ width: '100%' }}
              loading={nappiLoading}
              onSearch={setNappiSearchFilter}
              filterOption={false}
              notFoundContent={nappiLoading ? 'Searching...' : 'No medications found'}
            >
              {nappiData.map((med, index) => (
                <Select.Option key={index} value={med.genericName}>
                  {med.genericName}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>Start Date</label>
                <Button block onClick={() => setShowMedStartDatePicker(true)} className='desktop-date-button'>
                  {tempMedDates.startDate ? dayjs(tempMedDates.startDate).format('MMM YYYY') : 'Select start date'}
                </Button>
              </div>
            </Col>
            <Col span={12}>
              <div className='desktop-form-field'>
                <label>End Date (Optional)</label>
                <Button block onClick={() => setShowMedEndDatePicker(true)} className='desktop-date-button'>
                  {tempMedDates.endDate ? dayjs(tempMedDates.endDate).format('MMM YYYY') : 'Ongoing'}
                </Button>
              </div>
            </Col>
          </Row>

          <div className='desktop-modal-actions'>
            <Button onClick={() => setMedicationModalState({ show: false })}>Cancel</Button>
            <Button type='primary' onClick={handleAddMedication}>
              Add Medication
            </Button>
          </div>
        </div>
      </Modal>

      {/* Date Pickers */}
      <DatePicker
        isVisible={showIllnessDatePicker}
        onDateSelect={(date) => {
          setTempDiagnosisDate(date.toISOString())
          setShowIllnessDatePicker(false)
        }}
        onCancel={() => setShowIllnessDatePicker(false)}
        mode='month'
        maximumDate={new Date()}
      />

      <DatePicker
        isVisible={showMedStartDatePicker}
        onDateSelect={(date) => {
          setTempMedDates((prev) => ({ ...prev, startDate: date.toISOString() }))
          setShowMedStartDatePicker(false)
        }}
        onCancel={() => setShowMedStartDatePicker(false)}
        mode='month'
        maximumDate={new Date()}
      />

      <DatePicker
        isVisible={showMedEndDatePicker}
        onDateSelect={(date) => {
          setTempMedDates((prev) => ({ ...prev, endDate: date.toISOString() }))
          setShowMedEndDatePicker(false)
        }}
        onCancel={() => setShowMedEndDatePicker(false)}
        mode='month'
        maximumDate={new Date()}
      />

      <DesktopMedicalHistoryNav
        prevFunction={() => setFacet(null)}
        nextFunction={handleSubmit}
        prevText='Back to Overview'
        nextText='Save Chronic Illnesses'
      />
    </div>
  )
}

export default DesktopChronicIllnesses
